import { useEffect, useRef } from 'react'
import api from '../../../lib/api'
import { getRuntimeEnv } from '../../../lib/runtime-env'
import { notifySessionResumePublished } from '../../../lib/sessionResume'
import {
  isIdleBeyondThreshold,
  LAST_ACTIVITY_STORAGE_KEY,
  readStoredLastActivityAt,
  writeStoredLastActivityAt,
} from '../../../lib/sessionResumeActivity'

const DEFAULT_INACTIVITY_MINUTES = 15

function parseInactivityMinutes(): number {
  const raw = getRuntimeEnv('VITE_SESSION_RESUME_INACTIVITY_MINUTES')
  if (!raw) return DEFAULT_INACTIVITY_MINUTES
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_INACTIVITY_MINUTES
}

interface SessionResumeResponse {
  event_published: boolean
}

export function useSessionResume(enabled: boolean) {
  const lastActivityAtRef = useRef<number>(Date.now())
  const resumeInFlightRef = useRef(false)
  const inactivityMinutesRef = useRef(parseInactivityMinutes())

  useEffect(() => {
    if (!enabled) return

    const stored = readStoredLastActivityAt()
    if (stored != null) {
      lastActivityAtRef.current = stored
    } else {
      writeStoredLastActivityAt(lastActivityAtRef.current)
    }

    const touchActivity = (now: number) => {
      lastActivityAtRef.current = now
      writeStoredLastActivityAt(now)
    }

    const maybeResumeSession = async (now: number) => {
      if (resumeInFlightRef.current) return
      if (!isIdleBeyondThreshold(lastActivityAtRef.current, now, inactivityMinutesRef.current)) {
        return
      }

      resumeInFlightRef.current = true
      try {
        const { data } = await api.post<SessionResumeResponse>('/auth/session/resume')
        if (data.event_published) {
          notifySessionResumePublished()
        }
      } catch (error) {
        console.debug('[session-resume] request failed', error)
      } finally {
        touchActivity(Date.now())
        resumeInFlightRef.current = false
      }
    }

    const onUserActivity = () => {
      if (document.hidden) return
      const now = Date.now()
      void maybeResumeSession(now)
      touchActivity(now)
    }

    const onVisibilityChange = () => {
      if (document.hidden) return
      const now = Date.now()
      void maybeResumeSession(now)
      touchActivity(now)
    }

    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return
      onVisibilityChange()
    }

    const activityEvents: Array<keyof WindowEventMap> = [
      'pointerdown',
      'keydown',
      'scroll',
      'touchstart',
    ]

    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, onUserActivity, { passive: true })
    })
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pageshow', onPageShow)

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, onUserActivity)
      })
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pageshow', onPageShow)
      sessionStorage.removeItem(LAST_ACTIVITY_STORAGE_KEY)
    }
  }, [enabled])
}
